// 🔧 analytics.js

//----------------------------------------------------
// Google Analytics (Behavior Intelligence)
//----------------------------------------------------

/**
 * Tracks a Google Analytics event.
 *
 * ------------------------------------------------------------------
 * Supports both the legacy LGV event format and the new Engagement
 * Intelligence Engine parameter-rich format.
 *
 * Legacy Example:
 *
 * trackEvent({
 *   category: "Passport",
 *   action: "passport_started",
 *   label: "event123"
 * });
 *
 * New Example:
 *
 * trackEvent({
 *   action: "passport_stop_completed",
 *   parameters: {
 *      eventId,
 *      passportId,
 *      sponsorId,
 *      stopId,
 *      pointsAwarded
 *   }
 * });
 *
 * This implementation keeps every existing LGV page working while
 * allowing new Engagement Intelligence events to send rich metadata
 * into Google Analytics.
 * ------------------------------------------------------------------
 */

import ReactGA from "react-ga4";
import { GA4_ALLOWED_PARAMETERS, JOURNEY_CATALOG_VERSION, classifyJourneyRoute, resolveJourneyStage } from "../services/analytics/journey_event_catalog";
import { getLocalValue, LOCAL_KEYS, setLocalValue } from "./browser_storage_helpers";

const MEASUREMENT_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID || "";
let initialized = false;
const configured = () => Boolean(MEASUREMENT_ID);
const QUEUE_LIMIT = 50;
const QUEUE_TTL_MS = 24 * 60 * 60 * 1000;
const sentIds = new Set();
let eventContext = {};
const safePath = (value = "") => String(value).split("?")[0].split("#")[0].slice(0, 256) || "/";
const parameterAliases = { eventId: "event_id", eventSlug: "event_slug", sourcePage: "source_page", validationMethod: "validation_method", pointsAwarded: "points_awarded", votingType: "voting_type", itemType: "item_type", page: "source_page" };
const createEventId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
const readQueue = () => { try { const rows = JSON.parse(getLocalValue(LOCAL_KEYS.GA4_EVENT_QUEUE, "[]")); return Array.isArray(rows) ? rows.filter((row) => Date.now() - row.createdAt < QUEUE_TTL_MS) : []; } catch { return []; } };
const writeQueue = (rows) => setLocalValue(LOCAL_KEYS.GA4_EVENT_QUEUE, JSON.stringify(rows.slice(-QUEUE_LIMIT)));
const normalizeParameters = (parameters = {}) => Object.entries(parameters).reduce((result, [key, value]) => ({ ...result, [parameterAliases[key] || key]: value }), {});
const safeParameters = (parameters = {}) => Object.entries(parameters).reduce((result, [key, value]) => {
  if (!GA4_ALLOWED_PARAMETERS.has(key)) return result;
  if (typeof value === "number" && Number.isFinite(value)) result[key] = value;
  if (typeof value === "string") result[key] = key.includes("page") ? safePath(value) : value.slice(0, 128);
  return result;
}, { journey_catalog_version: JOURNEY_CATALOG_VERSION });

const enqueue = (event) => {
  const queue = readQueue();
  if (!queue.some((row) => row.id === event.id)) writeQueue([...queue, event]);
};

const send = (event) => {
  if (sentIds.has(event.id)) return true;
  try {
    ReactGA.event({ action: event.action, ...event.parameters });
    sentIds.add(event.id);
    return true;
  } catch (error) {
    return false;
  }
};

export const flushAnalyticsQueue = () => {
  if (!initialized || (typeof navigator !== "undefined" && !navigator.onLine)) return;
  const remaining = readQueue().filter((event) => !send(event));
  writeQueue(remaining);
};

export const initAnalytics = () => {
  if (!configured() || initialized) return false;
  ReactGA.initialize(MEASUREMENT_ID, { gtagOptions: { send_page_view: false } });
  initialized = true;
  if (typeof window !== "undefined") window.addEventListener("online", flushAnalyticsQueue, { once: true });
  flushAnalyticsQueue();
  return true;
};

export const setAnalyticsEventContext = ({ eventId, eventSlug } = {}) => {
  eventContext = {
    ...(typeof eventId === "string" && eventId ? { event_id: eventId } : {}),
    ...(typeof eventSlug === "string" && eventSlug ? { event_slug: eventSlug } : {}),
  };
};

export const trackEventVisit = ({ eventId, eventSlug, sourcePage } = {}) => {
  if (!eventId || typeof eventId !== "string") return;
  const now = Date.now();
  let visits = {};
  try {
    const saved = JSON.parse(getLocalValue(LOCAL_KEYS.GA4_EVENT_VISITS, "{}"));
    visits = saved && typeof saved === "object" && !Array.isArray(saved) ? saved : {};
  } catch { visits = {}; }
  const prior = visits[eventId] || { firstSeenAt: now, emitted: {} };
  const elapsedDays = Math.floor((now - prior.firstSeenAt) / (24 * 60 * 60 * 1000));
  const windowDays = elapsedDays >= 30 ? 30 : elapsedDays >= 7 ? 7 : elapsedDays >= 1 ? 1 : 0;
  if (windowDays && !prior.emitted?.[windowDays]) {
    trackEvent({ action: "event_returned", parameters: { event_id: eventId, event_slug: eventSlug, return_window: `${windowDays}d`, source_page: sourcePage } });
    prior.emitted = { ...(prior.emitted || {}), [windowDays]: true };
  }
  visits[eventId] = { ...prior, lastSeenAt: now };
  const bounded = Object.entries(visits).sort(([, left], [, right]) => Number(right.lastSeenAt || 0) - Number(left.lastSeenAt || 0)).slice(0, 50);
  setLocalValue(LOCAL_KEYS.GA4_EVENT_VISITS, JSON.stringify(Object.fromEntries(bounded)));
};

export const trackPage = (path) => {
  if (!initialized && !initAnalytics()) return;
  const page = safePath(path);
  ReactGA.send({ hitType: "pageview", page, journey_stage: classifyJourneyRoute(page), journey_catalog_version: JOURNEY_CATALOG_VERSION });
};

export const trackEvent = ({ action, parameters = {} }) => {
  if (!action) return;
  const eventName = String(action).slice(0, 40);
  const journeyStage = resolveJourneyStage(eventName);
  const event = { id: createEventId(), action: eventName, createdAt: Date.now(), parameters: safeParameters({ ...eventContext, ...normalizeParameters(parameters), journey_stage: journeyStage }) };
  if (!initialized && !initAnalytics()) { enqueue(event); return; }
  if (typeof navigator !== "undefined" && !navigator.onLine) { enqueue(event); return; }
  if (!send(event)) enqueue(event);
};
